
import { useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, UserRound, Book } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export const UserMenu = () => {
  const { session, profile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // First check if we actually have a session
    const { data: currentSession } = await supabase.auth.getSession();
    
    if (!currentSession.session) {
      console.log('No active session found, redirecting to home');
      navigate("/", { replace: true });
      return;
    }

    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      // Clear any local session state and navigate only after successful logout
      navigate("/", { replace: true });
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      
      toast({
        title: "Error logging out",
        description: error.message || "There was a problem logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getUserRoleDisplay = (role: string) => {
    switch (role) {
      case 'restaurant_owner':
        return 'Restaurant Owner';
      case 'admin':
        return 'Admin';
      default:
        return 'Customer';
    }
  };

  if (!session) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-white hover:text-white hover:bg-primary-foreground/10"
        onClick={() => navigate("/auth")}
      >
        Login
      </Button>
    );
  }

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:text-white hover:bg-primary-foreground/10"
          >
            <UserRound className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <DropdownMenuLabel>{getUserRoleDisplay(profile?.role || '')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {profile?.role === 'admin' && (
            <>
              <DropdownMenuItem onClick={() => navigate("/admin")}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Admin Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/admin/blog")}>
                <Book className="h-4 w-4 mr-2" />
                Manage Blog
              </DropdownMenuItem>
            </>
          )}
          {profile?.role === 'restaurant_owner' && (
            <DropdownMenuItem onClick={() => navigate("/dashboard")}>
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Restaurant Dashboard
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
